import { useEffect, useState } from "react";
import { HeartHandshake, Clock3, Users2, Sparkles, Mail } from "lucide-react";
import { api } from "../lib/api";

/**
 * Warm YTD tally card for counsellors — surfaces total donated hours, sessions and
 * unique clients helped so far this calendar year. Sits at the top of the counsellor
 * dashboard to reinforce that their contribution is seen, and to give ops the data
 * they need to eventually mint appreciation letters + tax certificates.
 */
export default function ContributionCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/counsellors/me/contribution")
      .then((r) => setData(r.data))
      .catch(() => setData({ year: new Date().getFullYear(), hours_donated: 0, sessions_delivered: 0, people_helped: 0 }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="brand-card p-5 mb-8 animate-pulse h-36" />;
  if (!data) return null;

  const noContrib = !data.sessions_delivered;
  const message = noContrib
    ? `Every session you accept this year (${data.year}) counts here — thank you for being part of Hello Foundation.`
    : `Thank you, ${data.year} donor. You've given ${data.hours_donated} hour${data.hours_donated === 1 ? "" : "s"} to ${data.people_helped} young ${data.people_helped === 1 ? "person" : "people"} so far — that's a real difference.`;

  return (
    <div
      className="brand-card mb-8 p-6 border-secondary/30 bg-gradient-to-br from-secondary/10 via-white to-primary/5"
      data-testid="contribution-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-secondary/20 text-secondary flex items-center justify-center shrink-0">
          <HeartHandshake size={20} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-extrabold text-secondary">Your contribution · {data.year}</div>
          <p className="text-sm text-[#3D405B] mt-1 leading-relaxed">{message}</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white/70 border border-[#3D405B]/10 p-4" data-testid="contribution-hours">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-bold text-[#7A7D96]"><Clock3 size={12} /> Hours donated</div>
          <div className="text-3xl font-extrabold text-[#3D405B] mt-1">{data.hours_donated}</div>
        </div>
        <div className="rounded-2xl bg-white/70 border border-[#3D405B]/10 p-4" data-testid="contribution-sessions">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-bold text-[#7A7D96]"><Sparkles size={12} /> Sessions delivered</div>
          <div className="text-3xl font-extrabold text-[#3D405B] mt-1">{data.sessions_delivered}</div>
        </div>
        <div className="rounded-2xl bg-white/70 border border-[#3D405B]/10 p-4" data-testid="contribution-people">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] font-bold text-[#7A7D96]"><Users2 size={12} /> People helped</div>
          <div className="text-3xl font-extrabold text-[#3D405B] mt-1">{data.people_helped}</div>
        </div>
      </div>
      <div className="text-[11px] text-[#7A7D96] mt-3 flex flex-wrap items-center justify-between gap-3">
        <div>Totals reset every 1 January. This letter is generated live from your session log — the board issues the final year-end letter.</div>
        <div className="flex gap-2">
          <a
            href={`${api.defaults.baseURL || ""}/counsellors/me/certificate?year=${data.year}&doc_type=appreciation`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-white text-xs font-extrabold shadow-sm hover:-translate-y-0.5 transition-all"
            data-testid="contribution-download-letter"
          >
            <Mail size={12} /> Download appreciation letter
          </a>
        </div>
      </div>
    </div>
  );
}
